import React, { useEffect, useState } from "react";
import {
  canUseNativeInstall,
  dismissInstallPrompt,
  isAndroid,
  isInstallDismissed,
  isIOS,
  isMobileDevice,
  isStandalone,
} from "../utils/pwa";

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mode, setMode] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || isInstallDismissed()) {
      return undefined;
    }

    if (!isMobileDevice()) {
      return undefined;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode("android-install");
    };

    const onInstalled = () => {
      setMode(null);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    if (isIOS()) {
      setMode("ios");
    } else if (isAndroid()) {
      const timer = window.setTimeout(() => {
        setMode((current) => current || "android-manual");
      }, 2500);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setMode(null);
      }
    } catch (err) {
      console.warn("Install prompt failed", err);
      setMode("android-manual");
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    dismissInstallPrompt();
    setDismissed(true);
    setMode(null);
  };

  if (isStandalone() || dismissed || !mode) {
    return null;
  }

  return (
    <div className="install-pwa" role="region" aria-label="Install app">
      <div className="install-pwa__card">
        <button
          type="button"
          className="install-pwa__close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          ×
        </button>

        <div className="install-pwa__icon" aria-hidden="true">
          R
        </div>

        <h2 className="install-pwa__title">Install Royallty</h2>
        <p className="install-pwa__desc">
          Use like an app from your home screen — works offline after first visit.
        </p>

        {mode === "android-install" && (
          <div className="install-pwa__actions install-pwa__actions--primary">
            <button
              type="button"
              className="btn btn-primary w-100 install-pwa__btn-main"
              onClick={handleInstall}
            >
              Install app
            </button>
          </div>
        )}

        {mode === "ios" && (
          <ol className="install-pwa__steps">
            <li>
              Tap <strong>Share</strong>
              <span className="install-pwa__share-icon" aria-hidden="true">
                ⎋
              </span>{" "}
              at the bottom of Safari
            </li>
            <li>
              Scroll and tap <strong>Add to Home Screen</strong>
            </li>
            <li>
              Tap <strong>Add</strong> in the top right
            </li>
          </ol>
        )}

        {mode === "android-manual" && (
          <ol className="install-pwa__steps">
            <li>
              Tap the <strong>menu ⋮</strong> in Chrome (top right)
            </li>
            <li>
              Tap <strong>Install app</strong> or{" "}
              <strong>Add to Home screen</strong>
            </li>
            {!canUseNativeInstall() && (
              <li className="install-pwa__note">
                Use <strong>HTTPS</strong> or open on your PC&apos;s localhost
                for one-tap install.
              </li>
            )}
          </ol>
        )}

        <button
          type="button"
          className="btn btn-link install-pwa__later"
          onClick={handleDismiss}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
